import { jsx as _jsx } from "react/jsx-runtime";
import { ShieldAlert } from 'lucide-react';
import { createModulePerms } from '@wit-support/ui-domain';
import { ChargebacksListPage } from './routes/list.js';
import { ChargebacksCasePage } from './routes/case.js';
import { ChargebacksAccountsPage } from './routes/accounts.js';
import { ChargebacksJournalsPage } from './routes/sync-runs.js';
import { ChargebacksDashboardPage } from './routes/dashboard.js';
import { ChargebacksRulesPage } from './routes/rules.js';
import { ChargebacksReactionRunsPage } from './routes/reaction-runs.js';
const { Provider: ChargebacksModulePermsProvider, usePerms: useChargebacksPerms } = createModulePerms({
    canIdentify: false,
    canAction: false,
    canManageAccounts: false,
    canManageStatus: false,
    canManageRules: false,
});
export { ChargebacksModulePermsProvider, useChargebacksPerms };
function ChargebacksListRoute() {
    const { canIdentify } = useChargebacksPerms();
    return _jsx(ChargebacksListPage, { canIdentify: canIdentify });
}
function ChargebacksAccountsRoute() {
    const { canManageAccounts } = useChargebacksPerms();
    return _jsx(ChargebacksAccountsPage, { canManage: canManageAccounts });
}
function ChargebacksCaseRoute() {
    const { canIdentify, canAction, canManageStatus } = useChargebacksPerms();
    return (_jsx(ChargebacksCasePage, { canIdentify: canIdentify, canAction: canAction, canManageStatus: canManageStatus }));
}
function ChargebacksRulesRoute() {
    const { canManageRules } = useChargebacksPerms();
    return _jsx(ChargebacksRulesPage, { canManage: canManageRules });
}
export const chargebacksModule = {
    id: 'chargebacks',
    label: 'Чарджбеки',
    icon: ShieldAlert,
    permission: 'chargebacks.read',
    workspace: 'risk',
    order: 10,
    description: 'Кейсы платёжного риска: чарджбеки, диспуты и алерты раннего предупреждения из Chargeblast.',
    showInNav: true,
    // Равноправные экраны под одним пунктом сайдбара. Переключатель рисует
    // шелл в навбаре — раздел не изобретает собственные вкладки в теле страницы.
    // Карточка кейса своей вкладки не имеет: в неё приходят из списка, и она
    // подсвечивает «Кейсы» как родителя.
    //
    // Вкладки «Настройки» здесь НЕТ и быть не должно: порог крупного плательщика
    // — общесистемное число, оно действует на инбокс, сводку и авто-пайплайны, а
    // не только на кейсы риска. Его место — единый раздел «Настройки»
    // (`@wit-support/admin-module-settings`); спрятанный третьим уровнем внутри
    // чужого раздела, он не находится.
    sections: [
        { path: '/', label: 'Кейсы' },
        // «Сводка» стоит сразу за кейсами: это обзорный экран того же материала, а
        // не отдельная служебная страница вроде аккаунтов и журналов. Роут закрыт
        // правом `dashboard.read` (супервайзер и владелец) — вкладку видит и
        // оператор первой линии, и экран объясняет ему отказ словами, а не 403.
        { path: '/dashboard', label: 'Сводка' },
        { path: '/rules', label: 'Правила' },
        { path: '/reaction-runs', label: 'Прогоны реакций' },
        { path: '/accounts', label: 'Аккаунты' },
        { path: '/journals', label: 'Журналы' },
    ],
    // Статические сегменты объявлены ДО динамического `$caseId` и выигрывают у
    // него по правилам роутера — `/chargebacks/accounts` никогда не попадёт в
    // карточку кейса с id «accounts».
    routes: [
        { path: '/', component: ChargebacksListRoute },
        { path: '/dashboard', component: ChargebacksDashboardPage, label: 'Сводка' },
        { path: '/rules', component: ChargebacksRulesRoute, label: 'Правила' },
        { path: '/reaction-runs', component: ChargebacksReactionRunsPage, label: 'Прогоны реакций' },
        { path: '/accounts', component: ChargebacksAccountsRoute, label: 'Аккаунты' },
        { path: '/journals', component: ChargebacksJournalsPage, label: 'Журналы' },
        { path: '/$caseId', component: ChargebacksCaseRoute, label: 'Кейс' },
    ],
};
export { ChargebacksListPage } from './routes/list.js';
export { IdentifyCustomerDialog } from './identify-dialog.js';
export { ChargebacksCasePage } from './routes/case.js';
export { ChargebacksAccountsPage } from './routes/accounts.js';
export { ChargebacksJournalsPage } from './routes/sync-runs.js';
export { ChargebacksDashboardPage } from './routes/dashboard.js';
export { ChargebacksRulesPage } from './routes/rules.js';
export { ChargebacksReactionRunsPage } from './routes/reaction-runs.js';
export { RuleDialog } from './forms/rule-dialog.js';
export { ReactionConditionEditor, summarizeReactionExpr, } from './forms/reaction-condition-editor.js';
export { RISK_CASE_REACTION_STATUS_RU, RISK_CASE_REACTION_STATUS_VARIANT, RISK_REACTION_RUN_KIND_RU, RISK_REACTION_RUN_STATUS_RU, RISK_REACTION_RUN_STATUS_VARIANT, RISK_REACTION_STEP_NAME_RU, RISK_REACTION_STEP_STATUS_RU, RISK_REACTION_STEP_STATUS_VARIANT, riskEscalationReasonRu, riskRunReasonRu, } from './reaction-labels.js';
export { setChargebacksApiBaseUrl, listCases, getCase, attachCaseIdentity, detachCaseIdentity, rematchCase, sendAlertAction, setCaseStatus, casesQueryString, parseAmountToCents, listAccounts, createAccount, updateAccount, deleteAccount, testAccount, listSyncRuns, listAlertActions, getSupportDashboard, supportDashboardQueryString, listRules, createRule, updateRule, deleteRule, reorderRules, listReactionRuns, reactCase, ChargebacksRequestError, CASES_PAGE_SIZE, JOURNAL_PAGE_SIZE, REACTION_RUNS_PAGE_SIZE, } from './api.js';
// Ключ сохранённых фильтров списка кейсов — наружу ради ОДНОГО вызова: шелл
// стирает его при выходе оператора (см. `AppShell`), иначе следующий человек за
// той же машиной унаследует чужой поиск по номеру кейса и последним 4 цифрам.
export { CHARGEBACKS_FILTERS_KEY, readCaseListFilters, writeCaseListFilters, hasActiveCaseFilters, } from './filters-storage.js';
//# sourceMappingURL=index.js.map